/**
 * Ant Design 主题映射
 * 将设计令牌转换为 Ant Design 主题配置
 */

import type { DesignTokens } from './types';
import type { ThemeConfig as AntdThemeConfig } from 'antd';

// 表单元素主题色（统一使用主色）
const FORM_THEME_COLOR = "#08C4C8";

/**
 * 将设计令牌转换为 Ant Design 主题配置
 * @param tokens 设计令牌
 * @returns Ant Design 主题配置对象
 */
export function toAntdTheme(tokens: DesignTokens): AntdThemeConfig {
  const { colors, typography, borderRadius, shadow } = tokens;

  return {
    token: {
      // 品牌色
      colorPrimary: colors.primary,
      colorSuccess: colors.success,
      colorWarning: colors.warning,
      colorError: colors.error,
      colorInfo: colors.info,

      // 文本颜色
      colorText: colors.text.primary,
      colorTextSecondary: colors.text.secondary,
      colorTextTertiary: colors.text.tertiary,
      colorTextQuaternary: colors.text.disabled,
      colorTextDisabled: colors.text.disabled,
      colorTextPlaceholder: '#A5B1C3', // Placeholder 统一颜色

      // 边框
      colorBorder: colors.border.base,
      colorBorderSecondary: colors.border.light,

      // 背景
      colorBgContainer: colors.background.elevated,
      colorBgLayout: colors.background.base,
      colorBgElevated: colors.background.elevated,
      colorBgSpotlight: colors.background.hover,

      // 字体
      fontFamily: typography.fontFamily.base,
      fontSize: parseInt(typography.fontSize.base),
      fontSizeSM: parseInt(typography.fontSize.sm),
      fontSizeLG: parseInt(typography.fontSize.lg),
      fontSizeXL: parseInt(typography.fontSize.xl),

      // 字重
      fontWeightStrong: typography.fontWeight.semibold,

      // 行高
      lineHeight: typography.lineHeight.base,
      lineHeightLG: typography.lineHeight.relaxed,
      lineHeightSM: typography.lineHeight.tight,

      // 圆角
      borderRadius: parseInt(borderRadius.base),
      borderRadiusSM: parseInt(borderRadius.sm),
      borderRadiusLG: parseInt(borderRadius.lg),
      borderRadiusXS: parseInt(borderRadius.sm),

      // 阴影
      boxShadow: shadow.base,
      boxShadowSecondary: shadow.sm,
      boxShadowTertiary: shadow.md,
    },

    components: {
      // Button 按钮
      Button: {
        colorPrimary: colors.primary,
        colorPrimaryHover: "#08c4c8",   // 主按钮悬停颜色
        colorPrimaryActive: "#06a09a",  // 主按钮激活颜色（稍深）
        colorLink: "#08c4c8",           // Link 按钮颜色
        colorLinkHover: "#08c4c8",      // Link 按钮悬停颜色
        colorLinkActive: "#06a09a",     // Link 按钮激活颜色（稍深）
        primaryShadow: `0 2px 0 ${colors.primary}33`,
        borderRadius: parseInt(borderRadius.base),
        controlHeight: 40, // iPad 友好的触摸目标
      },

      // Input 输入框
      Input: {
        colorBorder: colors.border.base,
        colorPrimary: FORM_THEME_COLOR,
        colorPrimaryHover: FORM_THEME_COLOR,
        borderRadius: parseInt(borderRadius.base),
        controlHeight: 40, // iPad 友好
      },

      // Select 选择器
      Select: {
        colorBorder: colors.border.base,
        colorPrimary: FORM_THEME_COLOR,
        colorPrimaryHover: FORM_THEME_COLOR,
        borderRadius: parseInt(borderRadius.base),
        controlHeight: 40, // iPad 友好
      },

      // InputNumber 数字输入框
      InputNumber: {
        colorBorder: colors.border.base,
        colorPrimary: FORM_THEME_COLOR,
        colorPrimaryHover: FORM_THEME_COLOR,
        borderRadius: parseInt(borderRadius.base),
        controlHeight: 40, // iPad 友好
      },

      // DatePicker 日期选择器
      DatePicker: {
        colorPrimary: FORM_THEME_COLOR,
        colorPrimaryHover: FORM_THEME_COLOR,
        borderRadius: parseInt(borderRadius.base),
        controlHeight: 40,
      },

      // TimePicker 时间选择器
      TimePicker: {
        colorPrimary: FORM_THEME_COLOR,
        colorPrimaryHover: FORM_THEME_COLOR,
        borderRadius: parseInt(borderRadius.base),
        controlHeight: 40,
      },

      // Checkbox 多选框
      Checkbox: {
        colorPrimary: FORM_THEME_COLOR,
        colorPrimaryHover: FORM_THEME_COLOR,
      },

      // Radio 单选框
      Radio: {
        colorPrimary: FORM_THEME_COLOR,
        colorPrimaryHover: FORM_THEME_COLOR,
      },

      // Switch 开关
      Switch: {
        colorPrimary: FORM_THEME_COLOR,
        colorPrimaryHover: FORM_THEME_COLOR,
      },

      // Cascader 级联选择
      Cascader: {
        colorPrimary: FORM_THEME_COLOR,
        colorPrimaryHover: FORM_THEME_COLOR,
        controlHeight: 40,
      },

      // TreeSelect 树选择
      TreeSelect: {
        colorPrimary: FORM_THEME_COLOR,
        colorPrimaryHover: FORM_THEME_COLOR,
        controlHeight: 40,
      },

      // Card 卡片
      Card: {
        borderRadiusLG: parseInt(borderRadius.lg),
        boxShadowTertiary: shadow.md,
      },

      // Modal 模态框
      Modal: {
        borderRadiusLG: parseInt(borderRadius.lg),
        boxShadow: shadow.xl,
      },

      // Table 表格
      Table: {
        headerBg: colors.background.hover,
        headerColor: colors.text.secondary,
        borderColor: colors.border.light,
      },

      // Form 表单
      Form: {
        labelColor: '#334255',
        labelFontSize: 14,
        labelHeight: 20,
      },

      // Message 消息提示
      Message: {
        contentBg: colors.background.elevated,
        boxShadow: shadow.lg,
      },

      // Notification 通知提醒
      Notification: {
        boxShadow: shadow.xl,
        borderRadiusLG: parseInt(borderRadius.lg),
      },

      // Drawer 抽屉
      Drawer: {
        borderRadiusLG: parseInt(borderRadius.lg),
      },

      // Menu 菜单
      Menu: {
        itemBg: 'transparent',
        itemSelectedBg: colors.background.active,
        itemSelectedColor: colors.primary,
        itemColor: colors.text.secondary,
        itemHoverBg: colors.background.hover,
        itemHoverColor: colors.primary,
      },

      // Tabs 标签页
      Tabs: {
        itemColor: "#475569",           // 未选中标签颜色
        itemSelectedColor: FORM_THEME_COLOR,   // 选中标签颜色
        itemHoverColor: FORM_THEME_COLOR,      // 悬停标签颜色
        itemActiveColor: FORM_THEME_COLOR,     // 激活标签颜色（active状态）
        inkBarColor: FORM_THEME_COLOR,         // 下划线颜色
      },
    },
  };
}

/**
 * 创建自定义 Ant Design 主题
 * 支持在自动映射的基础上进行额外定制
 * @param tokens 设计令牌
 * @param customConfig 自定义 Ant Design 配置
 * @returns 合并后的 Ant Design 主题配置
 */
export function createAntdTheme(
  tokens: DesignTokens,
  customConfig?: Partial<AntdThemeConfig>
): AntdThemeConfig {
  const baseTheme = toAntdTheme(tokens);

  if (!customConfig) {
    return baseTheme;
  }

  return {
    token: {
      ...baseTheme.token,
      ...customConfig.token,
    },
    components: {
      ...baseTheme.components,
      ...customConfig.components,
    },
    algorithm: customConfig.algorithm,
    hashed: customConfig.hashed,
    inherit: customConfig.inherit,
  };
}
